import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/weekday';

dayjs.extend(isSameOrBefore);
@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private snackBar = inject(MatSnackBar);

  isMobileDevice: boolean;
  monthDayFormat: 'MMM D' | 'D MMM' = 'MMM D';

  constructor() {
    this.isMobileDevice = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
    ].some((s) => navigator.userAgent.match(s));
  }

  launchOfflineNotification() {
    this.snackBar.open('Connection error: you may be offline.', 'Dismiss', {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'right',
    });
  }

  isLocalSafe(): boolean {
    return (
      typeof window === 'object' &&
      (window.location.href.startsWith('http://localhost:') ||
        window.location.href.startsWith('https://localhost:'))
    );
  }

  isEmail(input: string) {
    // The following regex is from the Angular forms library, licensed under MIT license.
    const EMAIL_REGEXP =
      /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const result = EMAIL_REGEXP.test(input);
    return result;
  }

  getStartOfWeek(time?: number) {
    return dayjs(time).startOf('week').valueOf();
  }

  getEndOfWeek(time?: number) {
    return dayjs(time).endOf('week').valueOf();
  }

  isMac() {
    return window.navigator.platform?.startsWith('Mac');
  }

  ctrlOrCmd(event: KeyboardEvent | MouseEvent) {
    return this.isMac() ? event.metaKey : event.ctrlKey;
  }

  ctrlOrCmdText() {
    return this.isMac() ? '⌘' : 'CTRL';
  }

  backspaceOrDeleteText() {
    return this.isMac() ? 'DELETE' : 'BACKSPACE';
  }

  taskFilterQueryParams(
    queryParams?: ActivatedRoute['snapshot']['queryParams']
  ): {
    userId: string | null | undefined;
    tagIds: string[] | null;
  } {
    const userIdQuery: string | string[] | undefined = queryParams?.userId;
    const userIdQuery2 =
      typeof userIdQuery === 'object' ? userIdQuery[0] : userIdQuery;
    const userId = userIdQuery2 === '0' ? null : userIdQuery2;

    const tagIdQuery: string | string[] | undefined = queryParams?.tagId;

    const tagIdQuery2 =
      (typeof tagIdQuery === 'string' ? [tagIdQuery] : tagIdQuery) ?? [];

    let tagIds: string[] | null;
    if (tagIdQuery2.length === 1 && tagIdQuery2[0] === '0') {
      tagIds = null;
    } else {
      tagIds = tagIdQuery2.filter((t) => t !== '0');
    }

    return { userId, tagIds };
  }

  /**
   * Returns the date/time without excessive information.
   */
  shortDateTimeFormat({
    time: inputTime,
    veryShort,
  }: {
    time: number;
    veryShort: boolean;
  }) {
    const now = dayjs();
    const time = dayjs(inputTime);
    const minutes = time.format('mm');

    const hideTimeForEventsWellIntoFuture = veryShort;

    // Today
    if (time.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')) {
      if (minutes === '00') return time.format('ha');
      else return time.format('h:mma');
    }
    // Tomorrow
    if (time.format('YYYY-MM-DD') === now.add(1, 'day').format('YYYY-MM-DD')) {
      if (minutes === '00') return time.format('ha') + ' tomorrow';
      else return time.format('h:mma') + ' tomorrow';
    }
    // This year
    if (time.year() === now.year()) {
      if (hideTimeForEventsWellIntoFuture) {
        return time.format(this.monthDayFormat);
      } else {
        if (minutes === '00') return time.format('ha ' + this.monthDayFormat);
        else return time.format('h:mma ' + this.monthDayFormat);
      }
    }
    // Future/Past years

    if (hideTimeForEventsWellIntoFuture) {
      if (minutes === '00') return time.format(this.monthDayFormat + ' YYYY');
      else return time.format(this.monthDayFormat + ' YYYY');
    } else {
      if (minutes === '00')
        return time.format('ha ' + this.monthDayFormat + ' YYYY');
      else return time.format('h:mma ' + this.monthDayFormat + ' YYYY');
    }
  }

  shortDateTimeRangeFormat({
    startAt,
    endAt,
    veryShort,
  }: {
    startAt: number;
    endAt?: number;
    veryShort: boolean;
  }) {
    if (!endAt) return this.shortDateTimeFormat({ time: startAt, veryShort });
    const now = dayjs();
    const tomorrow = now.add(1, 'day');
    const startObj = dayjs(startAt);
    const endObj = dayjs(endAt);

    const hideTimeForEventsWellIntoFuture = veryShort;

    let startComponent = '';
    let endComponent = '';
    let commonComponent = '';
    let spacer = ' - ';

    const startYear = startObj.year();
    const endYear = endObj.year();
    const currentYear = now.year();
    const startMonthDay = startObj.format(this.monthDayFormat);
    const endMonthDay = endObj.format(this.monthDayFormat);
    const currentMonthDay = now.format(this.monthDayFormat);
    const tomorrowMonthDay = tomorrow.format(this.monthDayFormat);
    const startAmPm = startObj.format('a');
    const endAmPm = endObj.format('a');
    if (startYear === endYear) {
      if (startYear !== currentYear) commonComponent = ' ' + startYear;
      // else don't show year
    } else {
      startComponent = ' ' + startYear;
      endComponent = ' ' + endYear;
    }

    if (startYear === endYear && startMonthDay === endMonthDay) {
      spacer = '-';
      if (startMonthDay === currentMonthDay) {
        // don't show anything
      } else if (startMonthDay === tomorrowMonthDay) {
        commonComponent = ' tomorrow'; // Replace year
      } else {
        if (this.monthDayFormat === 'MMM D' && commonComponent)
          commonComponent = ',' + commonComponent;
        commonComponent = ' ' + startMonthDay + commonComponent;
      }
    } else {
      if (this.monthDayFormat === 'MMM D') {
        if (startComponent) startComponent = ',' + startComponent;
        if (endComponent) endComponent = ',' + endComponent;
      }
      startComponent = ' ' + startMonthDay + startComponent;
      endComponent = ' ' + endMonthDay + endComponent;
    }

    if (
      startYear === endYear &&
      startMonthDay === endMonthDay &&
      startAmPm === endAmPm
    ) {
      commonComponent = startAmPm + commonComponent;
    } else {
      startComponent = startAmPm + startComponent;
      endComponent = endAmPm + endComponent;
    }

    if (
      !hideTimeForEventsWellIntoFuture ||
      startObj.format('YYYY-MM-DD') === now.format('YYYY-MM-DD') ||
      startObj.format('YYYY-MM-DD') === tomorrow.format('YYYY-MM-DD')
    ) {
      const hideMinutes =
        startObj.format('mm') === '00' && endObj.format('mm') === '00';
      if (!hideMinutes) {
        startComponent = ':' + startObj.format('mm') + startComponent;
        endComponent = ':' + endObj.format('mm') + endComponent;
      }
      startComponent = startObj.format('h') + startComponent;
      endComponent = endObj.format('h') + endComponent;
    }

    return startComponent
      ? startComponent + spacer + endComponent + commonComponent
      : commonComponent;
  }
}
